/**
 * BubbleButton Component
 * Clickable bubble-style button for user quick replies
 */

import React from 'react';
import { trackCtaClick, debugLog } from '../utils/marketing';
import './BubbleButton.css';

export interface BubbleButtonProps {
  /** Button label text */
  label: string;
  /** Optional icon or emoji */
  icon?: string;
  /** Click handler */
  onClick: () => void;
  /** Disabled state */
  disabled?: boolean;
  /** Custom className */
  className?: string;
  /** Optional URL for CTA buttons - opens in new tab (after whitelist validation) */
  url?: string;
  /** Original URL from flow config - used for tracking even if url is filtered */
  originalUrl?: string;
  /** Optional flag to mark this as a CTA button */
  isCTA?: boolean;
}

export const BubbleButton: React.FC<BubbleButtonProps> = ({
  label,
  icon,
  onClick,
  disabled = false,
  className = '',
  url,
  originalUrl,
  isCTA = false,
}) => {
  const handleClick = () => {
    console.log('[BubbleButton] handleClick called:', { label, isCTA, url, originalUrl, disabled });

    if (!disabled) {
      // For CTA buttons, emit tracking before opening
      if (isCTA) {
        console.log('[BubbleButton] This is a CTA button, calling trackCtaClick()');
        debugLog('CTA bubble click:', { label, url, originalUrl, isCTA });
        try {
          window.sendLpEvent?.('click');
        } catch (err) {
          void err;
        }
        // Always track CTA clicks, even if URL is filtered by whitelist
        trackCtaClick();

        // If validated URL is provided, open it after minimal delay
        if (url) {
          // Minimal delay (100ms) to allow pixel events to fire while keeping UX snappy
          setTimeout(() => {
            debugLog('Opening CTA URL (bubble):', url);
            window.open(url, '_blank', 'noopener,noreferrer');
            // Call onClick after navigation for state management
            onClick();
          }, 100);
        } else {
          // No validated URL (filtered by whitelist), but still call onClick
          // User feedback: tracking fired, but no navigation
          debugLog('CTA click tracked, but URL was filtered by whitelist');
          onClick();
        }
      } else {
        // Non-CTA buttons: immediate onClick
        onClick();

        // If URL is provided for non-CTA, open without delay
        if (url) {
          window.open(url, '_blank', 'noopener,noreferrer');
        }
      }
    }
  };

  const buttonClass = `bubble-button ${disabled ? 'bubble-button--disabled' : ''} ${isCTA ? 'bubble-button--cta cta_click' : ''} ${className}`;

  return (
    <button
      className={buttonClass}
      onClick={handleClick}
      disabled={disabled}
      type="button"
      aria-label={label}
    >
      {icon && <span className="bubble-button__icon">{icon}</span>}
      <span className="bubble-button__label">{label}</span>
    </button>
  );
};

export default BubbleButton;
